import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  Calendar,
  Clock,
  Heart,
  Loader,
  MapPin,
  Search,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import authAxios from "../../authAxios";
import instance from "../../axios";
import Toast from "../../components/common/Toast";
import { useGlobalState } from "../../GlobalStates";
import { Footer, StickyHeader } from "../../intro/components/landing-new";
import { uploadBigFileToS3 } from "../../services/uploads";
import FriendFormModal from "./components/FriendFormModal";
import InfoToast from "./components/InfoToast";
import ProfileDetailModal from "./components/ProfileDetailModal";

const SinglesInferno = () => {
  const [ideas, setIdeas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [toast, setToast] = useState(null);
  const profileRef = useRef(null);
  const formRef = useRef(null);
  const [votedIdeas, setVotedIdeas] = useState(new Set());
  const [minVotes, setMinVotes] = useState(4);
  const [newIdea, setNewIdea] = useState({
    name: "",
    description: "",
    telegram: "",
    images: [],
    imagePreviews: [],
  });

  const [user] = useGlobalState("user");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const search = urlParams.get("search");

    if (success === "true") {
      showToast(
        "我們已經列出您的朋友！分享此頁面連結，讓我們一起為您的朋友投票進入愛情島 <3",
        "celebration"
      );
    }

    if (search) {
      setSearchQuery(search);
    }
  }, []);

  useEffect(() => {
    fetchIdeas();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setShowForm(false);
      }
    };

    if (showForm) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showForm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setSelectedIdea(null);
      }
    };

    if (selectedIdea) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedIdea]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewIdea((prev) => ({
          ...prev,
          images: [...prev.images, file],
          imagePreviews: [...prev.imagePreviews, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setNewIdea((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imagePreviews: prev.imagePreviews.filter((_, i) => i !== index),
    }));
  };

  const fetchIdeas = async () => {
    try {
      const response = await instance.get("/v1/singlesinferno", {
        params: {
          userId: user.id,
        },
      });
      const profiles = response.data;
      setIdeas(profiles);

      // 按投票數降序排列個人資料並獲取第30名（如果存在）
      const sortedProfiles = [...profiles].sort((a, b) => b.votes - a.votes);
      const minPerson = sortedProfiles.length >= 30 ? sortedProfiles[29] : null;

      if (minPerson) {
        setMinVotes(minPerson.votes);
      }

      if (response.data.length > 0 && selectedIdea) {
        setSelectedIdea(
          response.data.find((idea) => idea.id === selectedIdea.id)
        );
      }
      setIsLoading(false);
    } catch (error) {
      console.error("獲取資料時出錯:", error);
      setIsLoading(false);
    }
  };

  const getVotedIdeas = async () => {
    try {
      const response = await authAxios.get("/v1/singlesinferno/getVotedIdeas");
      setVotedIdeas(
        new Set(response.data.map((idea) => idea.singleinfernos_id))
      );
    } catch (error) {
      console.error("獲取已投票資料時出錯:", error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      getVotedIdeas();
    }
  }, [user?.id]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleCheckout = async (uploaded_id, friend_name) => {
    // 呼叫後端創建會話
    const response = await instance.post("/v1/stripe/createCheckoutSession", {
      uploaded_id,
      friend_name,
    });

    const { url } = await response.data;

    // 重定向到Stripe結帳頁面
    // stripe.redirectToCheckout({ sessionId: url });
    window.location.href = url;
  };

  const handleVote = async (id) => {
    // const now = dayjs();
    // const votingOpens = dayjs("2025-02-16T12:00:00+08:00");

    // if (now.isBefore(votingOpens)) {
    //   showToast("投票將於2月16日中午12點開始！", "error");
    //   return;
    // }

    if (votedIdeas.has(id)) {
      return; // 如果已經投票則提前返回
    }

    try {
      setVotedIdeas((prev) => new Set([...prev, id])); // 添加到已投票集合
      await authAxios.post(`/v1/singlesinferno/${id}/vote`, {
        increment: true,
        userId: user.id,
      });
      await fetchIdeas();
      showToast("哇！看看你這個愛神丘比特！", "success");
    } catch (error) {
      showToast(error.response.data.error || "投票失敗。請再試一次。", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    if (newIdea.images.length === 0) {
      showToast("請至少上傳一張照片", "error");
      setLoadingSubmit(false);
      return;
    }

    // 驗證必填欄位
    if (!newIdea.name?.trim()) {
      showToast("請輸入您朋友的名字", "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newIdea.description?.trim()) {
      showToast("請告訴我們更多關於您朋友的資訊", "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newIdea.telegram?.trim()) {
      showToast("請提供您朋友的電話號碼", "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newIdea.gender) {
      showToast("請選擇您朋友的性別", "error");
      setLoadingSubmit(false);
      return;
    }

    try {
      // 先上傳所有圖片
      const uploadedImageUrls = await Promise.all(
        newIdea.images.map((image) => uploadBigFileToS3(image))
      );

      const profileData = {
        name: newIdea.name,
        description: newIdea.description,
        telegram: newIdea.telegram,
        gender: newIdea.gender,
        images: uploadedImageUrls,
        payment_intent: "tw_bali_inferno_2025",
      };

      if (user.id) {
        profileData.userId = user.id;
      }

      const uploadedProfile = await authAxios.post(
        "/v1/singlesinferno",
        profileData
      );

      // handleCheckout(uploadedProfile?.data?.id, newIdea.name);

      setShowForm(false);
      // setNewIdea({
      //   name: "",
      //   description: "",
      //   telegram: "",
      //   category: "",
      //   images: [],
      //   imagePreviews: [],
      // });

      showToast(
        "我們已收到您的提名！分享此頁面連結，讓我們一起為您的朋友投票進入愛情島 <3",
        "celebration"
      );

      await fetchIdeas();
    } catch (error) {
      console.error("提交資料時出錯:", error);
      showToast("推薦您的朋友失敗。錯誤: " + error, "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const filteredIdeas = ideas
    .filter(
      (idea) =>
        (idea.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          idea.name.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (!selectedCategory || idea.gender === selectedCategory)
    )
    .sort((a, b) => {
      if (selectedCategory !== "") {
        return b.votes - a.votes;
      }
      return 0;
    });

  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50 tw-pt-24 tw-pb-10 tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <Dialog
        open={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="tw-flex tw-justify-between tw-items-center">
            <span className="tw-text-2xl tw-font-bold">活動說明</span>
            <button
              onClick={() => setShowHowItWorks(false)}
              className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100"
            >
              <X className="tw-w-6 tw-h-6 tw-text-gray-500" />
            </button>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="tw-space-y-6 tw-py-4">
            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                1
              </div>
              <div>
                <h3 className="tw-font-semibold tw-mb-1">提名您的朋友</h3>
                <p className="tw-text-gray-600">
                  分享您朋友最好或最糟的照片，並告訴我們是什麼讓他們與眾不同。
                </p>
              </div>
            </div>

            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                2
              </div>
              <div>
                <h3 className="tw-font-semibold tw-mb-1">
                  在5月30日23:59前列出他們的資料
                </h3>
                <p className="tw-text-gray-600">
                  您只能列出一個資料，所以請明智選擇！
                </p>
              </div>
            </div>

            <div className="tw-flex tw-items-start tw-space-x-4">
              <div className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-pink-100 tw-text-pink-500">
                3
              </div>
              <div>
                <h3 className="tw-font-semibold tw-mb-1">
                  投票將於5月31日23:59結束
                </h3>
                <p className="tw-text-gray-600">
                  排名最高的男生和女生將獲得免費前往熱帶戀習曲－峇里島的旅行
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StickyHeader withoutCta />
      <div className="tw-max-w-4xl tw-mx-auto tw-min-h-[100vh]">
        <div className="tw-text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="tw-text-4xl tw-font-bold tw-bg-gradient-to-r tw-from-[#ff69b4] tw-to-[#ff4136] tw-bg-clip-text tw-text-transparent tw-mb-4"
          >
            Sotravel的煉獄 🏖
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="tw-flex tw-flex-col tw-items-center tw-space-y-2 tw-mb-8"
          >
            <div className="tw-flex tw-items-center tw-space-x-">
              <Clock className="tw-w-5 tw-h-5 tw-text-gray-500 tw-mr-3" />
              <p className="tw-text-gray-600">
                報名截止於{" "}
                <span className="tw-font-semibold">5月30日23:59</span>
              </p>
            </div>
            <div className="tw-flex tw-items-center tw-space-x-2">
              <Heart className="tw-w-5 tw-h-5 tw-text-rose-500" />
              <p className="tw-text-gray-600">
                投票結束於{" "}
                <span className="tw-font-semibold">5月31日23:59</span>
              </p>
            </div>
          </motion.div>

          <div className="tw-relative tw-w-full tw-max-w-4xl tw-mx-auto tw-mb-8">
            <img
              src="https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/178840photo_2024-09-17_19-31-17.jpg"
              alt="Singles Inferno"
              className="tw-w-full tw-h-[300px] md:tw-h-[670px] tw-object-cover tw-rounded-2xl tw-shadow-lg"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="tw-text-center tw-max-w-2xl tw-mx-auto tw-mb-12"
          >
            <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
              關於活動
            </h2>
            <div className="tw-flex tw-items-center tw-justify-center tw-space-x-2 tw-mb-4">
              <Calendar className="tw-w-5 tw-h-5 tw-text-gray-500" />
              <p className="tw-text-gray-600">
                活動日期 <span className="tw-font-semibold">8月15-18日</span>
              </p>
            </div>
            <div className="tw-space-y-4 tw-text-gray-600">
              <p>
                搭上我們的愛情快艇，前往峇里島展開四天三夜的浪漫冒險。從追海豚到跳瀑布，遊戲瘋、笑聲響、愛情火花啪啦啪啦炸！
              </p>
              <p className="tw-italic tw-text-pink-500 tw-font-medium">
                小心：你可能會濕身 💦
              </p>
            </div>
          </motion.div>
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-mb-8">
            <div className="tw-flex tw-flex-row tw-gap-4">
              <button
                onClick={() => {
                  if (!localStorage.getItem("USER_ID")) {
                    window.location.href =
                      "https://www.sotravel.com/login?reroute=/inferno";
                    return;
                  }
                  setShowForm(!showForm);
                }}
                className="tw-bg-gradient-to-r tw-from-[#ff69b4] tw-to-[#ff4136] tw-text-white tw-px-8 tw-py-4 tw-rounded-full tw-shadow-lg tw-font-semibold hover:tw-shadow-xl tw-transition-all"
              >
                推薦朋友
              </button>
            </div>
            <button
              onClick={() => setShowHowItWorks(true)}
              className="tw-text-gray-600 hover:tw-text-gray-900 tw-underline tw-transition-colors"
            >
              活動說明
            </button>
          </div>

          <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2 tw-mb-7 tw-mt-14">
            <button
              onClick={() => setSelectedCategory("")}
              className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-transition-all
                ${
                  !selectedCategory
                    ? "tw-bg-[#00bfff] tw-text-white"
                    : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
                }`}
            >
              <span className="tw-mr-[7px]">💃</span>

              {isMobile ? <span>最新列出</span> : <span>最新列出的單身者</span>}
            </button>
            <button
              onClick={() => setSelectedCategory("male")}
              className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-transition-all tw-flex tw-items-center tw-space-x-2
                ${
                  selectedCategory === "male"
                    ? "tw-bg-[#00bfff] tw-text-white"
                    : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
                }`}
            >
              <span>👑</span>
              {isMobile ? <span>男生</span> : <span>男生排行榜</span>}
            </button>
            <button
              onClick={() => setSelectedCategory("female")}
              className={`tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-transition-all tw-flex tw-items-center tw-space-x-2
                ${
                  selectedCategory === "female"
                    ? "tw-bg-[#00bfff] tw-text-white"
                    : "tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50"
                }`}
            >
              <span>👸</span>
              {isMobile ? <span>女生</span> : <span>女生排行榜</span>}
            </button>
            <div className="tw-relative">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-w-4 tw-h-4" />
              <input
                type="text"
                placeholder="尋找你的心動對象 😮‍💨"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-w-full tw-pl-10 tw-pr-4 tw-py-2 tw-rounded-full tw-border-0 tw-bg-white tw-shadow focus:tw-ring-2 focus:tw-ring-[deepskyblue] tw-transition-all tw-text-sm"
              />
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {showForm && (
            <FriendFormModal
              onClose={() => setShowForm(false)}
              newIdea={newIdea}
              setNewIdea={setNewIdea}
              handleSubmit={handleSubmit}
              loadingSubmit={loadingSubmit}
              handleImageChange={handleImageChange}
              handleRemoveImage={handleRemoveImage}
            />
          )}

          {selectedIdea && (
            <ProfileDetailModal
              selectedIdea={selectedIdea}
              onClose={() => setSelectedIdea(null)}
              handleVote={handleVote}
              votedIdeas={votedIdeas}
              showToast={showToast}
            />
          )}

          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}

          {/* <InfoToast minVotes={minVotes} /> */}
        </AnimatePresence>

        <div className="tw-space-y-4">
          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Loader className="tw-w-8 tw-h-8 tw-text-[deepskyblue]" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mt-4 tw-text-gray-500"
              >
                正在載入冒險...
              </motion.p>
            </div>
          ) : filteredIdeas.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="tw-text-center tw-py-12"
            >
              <motion.div
                animate={{
                  y: [0, -10, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <MapPin className="tw-w-16 tw-h-16 tw-text-gray-300 tw-mx-auto tw-mb-4" />
              </motion.div>
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2"
              >
                未找到單身者
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="tw-text-gray-500"
              >
                嘗試調整您的搜索或推薦您的朋友！
              </motion.p>
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredIdeas.map((idea, index) => (
                <motion.div
                  layout="position"
                  key={idea.id}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -20 }}
                  transition={{
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.3 },
                    layout: { duration: 0.3 },
                    delay: index * 0.1,
                  }}
                  className="tw-bg-white tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-shadow tw-overflow-hidden"
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
                    <div
                      className="tw-flex tw-items-center"
                      onClick={() => setSelectedIdea(idea)}
                    >
                      <div className="tw-w-16 tw-h-16 tw-rounded-full tw-overflow-hidden tw-mr-3 tw-flex-shrink-0">
                        <img
                          src={idea.images[0]}
                          alt=""
                          className="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                        />
                      </div>
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-font-medium tw-text-lg">
                          {idea.name}
                        </span>
                        <span className="tw-text-sm tw-text-gray-500">
                          由 {idea.author || "匿名"} 推薦
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(
                          `https://sotravel.com/inferno?search=${idea.name}`
                        );
                        showToast("已複製個人資料連結！", "success");
                      }}
                      className="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 tw-flex tw-items-center tw-gap-1"
                    >
                      <span>分享資料</span>
                      <ArrowUpRight className="tw-w-5 tw-h-5" />
                    </button>
                  </div>

                  <div className="tw-p-3">
                    <p
                      className="tw-text-gray-600 tw-mb-2 tw-line-clamp-2"
                      onClick={() => setSelectedIdea(idea)}
                    >
                      {idea.description}
                    </p>
                    <div className="tw-flex tw-items-center tw-justify-between tw-my-4">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        className="tw-flex tw-items-center tw-space-x-1"
                        onClick={() => handleVote(idea.id, true)}
                      >
                        <Heart
                          className={`tw-w-6 tw-h-6 ${
                            votedIdeas.has(idea.id)
                              ? "tw-fill-rose-500 tw-text-rose-500"
                              : "tw-text-rose-500"
                          }`}
                        />
                        <span className="tw-font-medium">
                          {idea.votes} {idea.votes === 1 ? "票" : "票"}
                        </span>
                      </motion.button>

                      <button
                        className="tw-text-pink-500 hover:tw-text-blue-700 tw-font-medium"
                        onClick={() => setSelectedIdea(idea)}
                      >
                        查看更多
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SinglesInferno;
